import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

// Define image size presets
const IMAGE_PRESETS = {
  thumbnail: {
    width: 80,
    quality: 'eco',
    blur: 1000
  },
  card: {
    width: 120,
    quality: 'low',
    blur: 800
  },
  preview: {
    width: 200,
    quality: 'good',
    blur: 500
  }
} as const;

type ImageSize = keyof typeof IMAGE_PRESETS;

// Update Props interface
interface ProgressiveImageProps {
  src: string;
  alt: string;
  className?: string;
  size?: ImageSize;
  blur?: boolean;
  onLoad?: () => void;
}

const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
  src,
  alt,
  className = '',
  size = 'card',
  blur = true,
  onLoad
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState('');
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '50px 0px'
  });

  useEffect(() => {
    if (!inView || !src) return;

    // Avoid setState on a component that unmounted while images were loading
    let cancelled = false;

    const getOptimizedUrl = (url: string, presetSize: ImageSize): string => {
      const preset = IMAGE_PRESETS[presetSize];
      return url.replace(
        '/upload/',
        `/upload/w_${preset.width},q_auto:${preset.quality}/`
      );
    };

    const loadFullImage = () => {
      const fullImg = new Image();
      fullImg.onload = () => {
        if (cancelled) return;
        setCurrentSrc(getOptimizedUrl(src, size));
        setIsLoaded(true);
        onLoad?.();
      };
      fullImg.onerror = () => {
        if (cancelled) return;
        // Optimized URL failed — fall back to the original source
        setCurrentSrc(src);
        setIsLoaded(true);
        onLoad?.();
      };
      fullImg.src = getOptimizedUrl(src, size);
    };

    // Generate tiny placeholder
    const placeholder = src.replace(
      '/upload/',
      `/upload/w_${IMAGE_PRESETS[size].width / 4},e_blur:${IMAGE_PRESETS[size].blur}/`
    );

    // Load placeholder first, then the optimized image
    const placeholderImg = new Image();
    placeholderImg.onload = () => {
      if (cancelled) return;
      setCurrentSrc(placeholder);
      loadFullImage();
    };
    placeholderImg.onerror = () => {
      if (cancelled) return;
      // Placeholder failed (e.g. not a transformable URL) — skip straight to full image
      loadFullImage();
    };
    placeholderImg.src = placeholder;

    return () => {
      cancelled = true;
    };
  }, [inView, src, size, onLoad]);

  return (
    <div
      ref={ref}
      className={`overflow-hidden ${className}`}
    >
      {currentSrc && (
        <img
          src={currentSrc}
          alt={alt}
          loading="lazy"
          className={`
            w-full h-full object-cover transition-all duration-500
            ${blur && !isLoaded ? 'blur-lg scale-105' : 'blur-0 scale-100'}
          `}
        />
      )}
    </div>
  );
};

export default ProgressiveImage;
